import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { SelectDropdown } from '../shared/SelectDropdown';

/**
 * The minimum a project must expose to be a move destination. Deliberately
 * structural rather than the full Project type: the dialog is rendered from
 * two different pages whose project rows are selected differently, and it has
 * no business demanding fields it never reads.
 */
export interface MoveDestinationProject {
  id: string;
  name: string;
  archivedAt?: string | Date | null;
  isDemo?: boolean;
}

interface MoveIssuesDialogProps {
  open: boolean;
  issueIds: string[];
  /** The project the issues are in now — never offered as a destination. */
  currentProjectId: string | null;
  projects: MoveDestinationProject[];
  onMove: (targetProjectId: string, targetProjectName: string) => void;
  onCancel: () => void;
  moving: boolean;
}

/**
 * Destination picker for "Move to project".
 *
 * WHY THIS IS A CONFIRMATION AND NOT AN INLINE SELECT.
 * A move is the only issue action with consequences the user cannot see and
 * cannot undo by repeating it backwards:
 *   - the server re-mints the issue's displayId from the destination project's
 *     prefix, so OQ-127 becomes something like CR-4. Every link that named the
 *     old handle — a Jira description, a Slack card, a bookmarked URL — stops
 *     resolving, and moving the issue back mints a THIRD id rather than
 *     restoring the first.
 *   - the group assignment is dropped, because groups belong to the project
 *     being left.
 * Neither is visible in a list row. Saying it here is the only chance the user
 * gets to decline.
 *
 * The destination list is same-team by construction: it renders whatever the
 * caller's project query returned, which is already scoped to the active team,
 * and the server refuses a cross-team destination outright.
 */
export function MoveIssuesDialog({
  open,
  issueIds,
  currentProjectId,
  projects,
  onMove,
  onCancel,
  moving,
}: MoveIssuesDialogProps) {
  const [targetProjectId, setTargetProjectId] = useState('');

  const destinations = projects.filter(
    (p) => p.id !== currentProjectId && !p.archivedAt && !p.isDemo,
  );

  // Preselect the only real choice. With one destination a picker is a
  // formality, and making the user open it to choose the single option is
  // friction that teaches nothing.
  useEffect(() => {
    if (!open) {
      setTargetProjectId('');
      return;
    }
    if (destinations.length === 1) setTargetProjectId(destinations[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, destinations.length]);

  if (!open) return null;

  const count = issueIds.length;
  const countLabel = `${count} issue${count === 1 ? '' : 's'}`;

  // Nothing to move to. An empty picker would read as a broken control, so the
  // dialog explains the state and offers only a way out.
  if (destinations.length === 0) {
    return (
      <ConfirmDialog
        open
        title={`Move ${countLabel}`}
        message="This is your only project, so there is nowhere to move these issues yet. Create another project first."
        confirmLabel="Got it"
        onConfirm={onCancel}
        onCancel={onCancel}
        hideCancel
        maxWidthClass="max-w-[432px]"
      />
    );
  }

  const chosen = destinations.find((p) => p.id === targetProjectId);

  return (
    <ConfirmDialog
      open
      title={`Move ${countLabel} to another project?`}
      confirmLabel={`Move ${countLabel}`}
      confirmDisabled={!chosen}
      loading={moving}
      onConfirm={() => { if (chosen) onMove(chosen.id, chosen.name); }}
      onCancel={onCancel}
      maxWidthClass="max-w-[432px]"
    >
      <div className="mb-4">
        <label className="field-label" htmlFor="move-issues-project">Project</label>
        <SelectDropdown
          value={targetProjectId}
          options={destinations.map((p) => ({ value: p.id, label: p.name }))}
          onChange={setTargetProjectId}
          placeholder="Choose a project…"
          aria-label="Project"
          size="lg"
        />
      </div>
      <p className="field-hint mb-5">
        {chosen
          ? `These issues get new IDs in ${chosen.name}. Their current IDs will be reissued and old links to them stop working. Any group assignment is cleared.`
          : 'Moved issues get new IDs in their destination. Their current IDs will be reissued and old links to them stop working. Any group assignment is cleared.'}
      </p>
    </ConfirmDialog>
  );
}
